import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import type { AddWorkOrderItemInput, SkuSearchResult } from '../../types';
import { useCatalogSkuSearch } from '../../hooks/useWmsSkus';
import { useDebounce } from '../../hooks/useDebounce';

interface AddItemModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AddWorkOrderItemInput) => Promise<void>;
  isLoading?: boolean;
}

export function AddItemModal({
  open,
  onClose,
  onSubmit,
  isLoading,
}: AddItemModalProps) {
  const [skuSearch, setSkuSearch] = useState('');
  const [selectedSku, setSelectedSku] = useState<SkuSearchResult | null>(null);
  const [quantityOrdered, setQuantityOrdered] = useState(1);
  const [unitPrice, setUnitPrice] = useState('');
  const [notes, setNotes] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSkuSearch('');
      setSelectedSku(null);
      setQuantityOrdered(1);
      setUnitPrice('');
      setNotes('');
    }
  }, [open]);

  const debouncedSearch = useDebounce(skuSearch, 300);
  const { data: skuResults } = useCatalogSkuSearch(debouncedSearch);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSku || quantityOrdered < 1) return;

    // Safely parse unit price
    let parsedPrice: number | undefined;
    if (unitPrice) {
      const price = parseFloat(unitPrice);
      if (!isNaN(price) && isFinite(price) && price >= 0) {
        parsedPrice = price;
      }
    }

    try {
      await onSubmit({
        mongo_sku_id: selectedSku.id,
        sku_code: selectedSku.sku,
        sku_name: selectedSku.nameEn || selectedSku.nameFr,
        quantity_ordered: quantityOrdered,
        unit_price: parsedPrice,
        notes: notes || undefined,
      });

      setSkuSearch('');
      setSelectedSku(null);
      setQuantityOrdered(1);
      setUnitPrice('');
      setNotes('');
      toast.success('Item added successfully');
      onClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to add item: ${message}`);
      console.error('Error adding item:', error);
    }
  };

  const handleSelectSku = (sku: SkuSearchResult) => {
    setSelectedSku(sku);
    setSkuSearch(sku.sku);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Item</DialogTitle>
          <DialogDescription>
            Add a SKU item to this work order.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">SKU *</label>
            <div className="relative mt-1">
              <Input
                value={skuSearch}
                onChange={(e) => {
                  setSkuSearch(e.target.value);
                  if (selectedSku && e.target.value !== selectedSku.sku) {
                    setSelectedSku(null);
                  }
                }}
                placeholder="Search for SKU..."
              />
              {skuResults && skuResults.length > 0 && !selectedSku && (
                <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {skuResults.map((sku) => (
                    <button
                      key={sku.id}
                      type="button"
                      className="w-full px-3 py-2 text-left hover:bg-accent text-sm"
                      onClick={() => handleSelectSku(sku)}
                    >
                      <div className="font-mono font-medium">{sku.sku}</div>
                      <div className="text-muted-foreground text-xs truncate">
                        {sku.nameEn || sku.nameFr}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedSku && (
              <p className="mt-1 text-sm text-muted-foreground">
                {selectedSku.manufacturerName ? `${selectedSku.manufacturerName} · ` : ''}
                {selectedSku.nameEn || selectedSku.nameFr}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Quantity *</label>
              <Input
                type="number"
                min={1}
                value={quantityOrdered}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setQuantityOrdered(1);
                    return;
                  }
                  const parsed = parseInt(value, 10);
                  if (!isNaN(parsed) && parsed >= 1) {
                    setQuantityOrdered(parsed);
                  }
                }}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Unit Price</label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                placeholder="0.00"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Notes</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
              className="mt-1"
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !selectedSku || quantityOrdered < 1}
            >
              {isLoading ? 'Adding...' : 'Add Item'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
